/* eslint-disable @typescript-eslint/no-inferrable-types */
import { Component, Input, OnInit, OnDestroy, ElementRef, Renderer2, ViewChild, inject } from '@angular/core';
import { CdsLangService } from '@cds/ng-core/lang';
import { CdsIconModule } from '@cds/ng-web-components/icon';

export interface TooltipItem {
  code: string;
  description: string;
  // 添加国际化支持的可选字段
  codeI18nKey?: string;
  descriptionI18nKey?: string;
}

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

@Component({
  selector: 'app-custom-tooltip',
  templateUrl: './custom-tooltip.component.html',
  styleUrls: ['./custom-tooltip.component.scss'],
  imports: [CdsIconModule],
})
export class CustomTooltipComponent implements OnInit, OnDestroy {
  private static activeTooltipCloser: (() => void) | null = null;

  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private langService = inject(CdsLangService);

  @Input() tooltipItems: TooltipItem[] = [];
  @Input() title: string = 'Sub Account Type Reference';
  @Input() interactive = true;
  @Input() placement: TooltipPlacement = 'bottom';
  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLElement>;

  isVisible = false;
  private globalTooltipElement: HTMLElement | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private tooltipListenerCleanups: Array<() => void> = [];
  private tooltipCloser: (() => void) | null = null;
  private isBridgeActive = false;

  ngOnInit(): void {
    this.parseTooltipString();
  }

  ngOnDestroy(): void {
    this.clearTimers();
    this.hideGlobalTooltip();
  }

  private clearTimers(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  private scheduleHide(delay: number): void {
    if (this.hideTimer) return;

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.hideGlobalTooltip();
    }, delay);
  }

  private clearTooltipListeners(): void {
    this.tooltipListenerCleanups.forEach(cleanup => cleanup());
    this.tooltipListenerCleanups = [];
  }

  private removeOrphanedGlobalTooltips(): void {
    document.querySelectorAll('.global-tooltip-panel').forEach(element => {
      if (element !== this.globalTooltipElement) {
        element.remove();
      }
    });
  }

  private getTriggerElement(): HTMLElement {
    return this.triggerRef?.nativeElement ?? (this.elementRef.nativeElement as HTMLElement);
  }

  private getTooltipHost(): HTMLElement {
    return this.getTriggerElement().closest<HTMLElement>('.cdk-overlay-pane') ?? document.body;
  }

  private isPointerInside(event: MouseEvent, element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
  }

  private isEventInsideElement(event: MouseEvent, element: HTMLElement): boolean {
    return this.isPointerInside(event, element);
  }

  private closeFromOutside(): void {
    if (this.isBridgeActive) return;

    this.hideGlobalTooltip();
  }

  private parseTooltipString(): void {
    // 默认的subAccountType数据
    const defaultTooltipData = [
      { code: 'MC', description: 'Including EE and ER Portion', descriptionI18nKey: 'ee.subaccount.MC' },
      { code: 'VC', description: 'Including EE and ER Portion', descriptionI18nKey: 'ee.subaccount.VC' },
      { code: 'ERMC', description: 'Employer mandatory contributions', descriptionI18nKey: 'ee.subaccount.ERMC' },
      { code: 'EEMC', description: 'Employee mandatory contributions', descriptionI18nKey: 'ee.subaccount.EEMC' },
      { code: 'ERVC', description: 'Employer voluntary contributions 1', descriptionI18nKey: 'ee.subaccount.ERVC' },
      { code: 'ERVC2', description: 'Employer voluntary contributions 2', descriptionI18nKey: 'ee.subaccount.ERVC2' },
      { code: 'ERVC3', description: 'Employer voluntary contributions 3', descriptionI18nKey: 'ee.subaccount.ERVC3' },
      { code: 'ERVC4', description: 'Employer voluntary contributions 4', descriptionI18nKey: 'ee.subaccount.ERVC4' },
      { code: 'ERVC5', description: 'Employer voluntary contributions 5', descriptionI18nKey: 'ee.subaccount.ERVC5' },
      { code: 'ERVC6', description: 'Employer voluntary contributions 6', descriptionI18nKey: 'ee.subaccount.ERVC6' },
      { code: 'ERVC7', description: 'Employer voluntary contributions 7', descriptionI18nKey: 'ee.subaccount.ERVC7' },
      { code: 'EEVC', description: 'Employee voluntary contributions 1', descriptionI18nKey: 'ee.subaccount.EEVC' },
      { code: 'EEVC2', description: 'Employee voluntary contributions 2', descriptionI18nKey: 'ee.subaccount.EEVC2' },
      { code: 'EEVC3', description: 'Employee voluntary contributions 3', descriptionI18nKey: 'ee.subaccount.EEVC3' },
      { code: 'FRMC', description: 'Mandatory contributions from former employment(s)', descriptionI18nKey: 'ee.subaccount.FRMC' },
      { code: 'FRVC', description: 'Voluntary contributions from former employments(s)', descriptionI18nKey: 'ee.subaccount.FRVC' },
      { code: 'FORFEITURE', description: 'Forfeiture', descriptionI18nKey: 'ee.subaccount.FORFEITURE' },
      { code: 'RESERVE', description: 'Reserve', descriptionI18nKey: 'ee.subaccount.RESERVE' },
      { code: 'TVC', description: 'Tax Deductible Voluntary Contributions', descriptionI18nKey: 'ee.subaccount.TVC' },
      { code: 'SVC', description: 'Special Voluntary Contributions', descriptionI18nKey: 'ee.subaccount.SVC' },
      { code: 'SEPMC', description: 'Self-employed Persons mandatory contributions', descriptionI18nKey: 'ee.subaccount.SEPMC' },
      { code: 'SEPVC', description: 'Self-employed Persons voluntary contributions', descriptionI18nKey: 'ee.subaccount.SEPVC' },
      { code: 'PREMPFER', description: 'Pre MPF Sevice ER Benefits', descriptionI18nKey: 'ee.subaccount.PREMPFER' },
      { code: 'PREMPFER2', description: 'Pre MPF Service ER Benefits 2', descriptionI18nKey: 'ee.subaccount.PREMPFER2' },
      { code: 'POSTMPFER', description: 'Post MPF Service ER Benefits', descriptionI18nKey: 'ee.subaccount.POSTMPFER' },
      { code: 'POSTMPFER2', description: 'Post MPF Service ER Benefits 2', descriptionI18nKey: 'ee.subaccount.POSTMPFER2' },
      { code: 'PREMPFEE', description: 'Pre MPF Service EE Benefits', descriptionI18nKey: 'ee.subaccount.PREMPFEE' },
      { code: 'PREMPFEE2', description: 'Pre MPF Service EE Benefits 2', descriptionI18nKey: 'ee.subaccount.PREMPFEE2' },
      { code: 'POSTMPFEE', description: 'Post MPF Service EE Benefits', descriptionI18nKey: 'ee.subaccount.POSTMPFEE' },
      { code: 'POSTMPFEE2', description: 'Post MPF Service EE Benefits 2', descriptionI18nKey: 'ee.subaccount.POSTMPFEE2' },
      { code: 'OPT', description: 'Optional Contributions (apply on ORSO & Macau Plan only)', descriptionI18nKey: 'ee.subaccount.OPT' },
    ];

    this.tooltipItems = this.tooltipItems.length > 0 ? this.tooltipItems : defaultTooltipData;
  }

  onMouseEnter(): void {
    this.isBridgeActive = false;
    this.clearHideTimer();

    // 如果已经显示，则不需要再次显示
    if (this.globalTooltipElement || this.showTimer) return;

    // 设置显示延迟 - 300ms
    this.showTimer = setTimeout(() => {
      this.showGlobalTooltip();
      this.showTimer = null;
    }, 300);
  }

  onMouseLeave(): void {
    // 清除显示定时器
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }

    if (!this.interactive) {
      this.hideGlobalTooltip();
      return;
    }

    this.isBridgeActive = true;
    this.scheduleHide(250);
  }

  private showGlobalTooltip(): void {
    if (this.globalTooltipElement) return;

    if (CustomTooltipComponent.activeTooltipCloser && CustomTooltipComponent.activeTooltipCloser !== this.tooltipCloser) {
      CustomTooltipComponent.activeTooltipCloser();
    }
    this.tooltipCloser = () => this.hideGlobalTooltip();
    CustomTooltipComponent.activeTooltipCloser = this.tooltipCloser;
    this.removeOrphanedGlobalTooltips();

    // 创建tooltip元素
    const tooltipElement = this.renderer.createElement('div') as HTMLElement;
    this.globalTooltipElement = tooltipElement;
    this.renderer.addClass(tooltipElement, 'global-tooltip-panel');

    // 生成内容HTML
    const itemsHtml = this.tooltipItems
      .map(item => {
        // 使用 i18n key 如果提供，否则使用原始值
        const displayCode = item.codeI18nKey ? this.langService.translate(item.codeI18nKey) : item.code;
        const displayDescription = item.descriptionI18nKey ? this.langService.translate(item.descriptionI18nKey) : item.description;

        return `
            <div class="global-tooltip-item">
              <div class="global-item-code">${displayCode}</div>
              <div class="global-item-description">${displayDescription}</div>
            </div>
          `;
      })
      .join('');

    // 设置内容
    tooltipElement.innerHTML = `
        <div class="global-tooltip-header">
          <h4 class="global-tooltip-title">${this.title}</h4>
        </div>
        <div class="global-tooltip-content">
          <div class="global-content-list">
            ${itemsHtml}
          </div>
        </div>
      `;

    this.renderer.appendChild(this.getTooltipHost(), tooltipElement);

    // 计算并设置位置
    this.positionGlobalTooltip();

    if (!this.interactive) {
      this.renderer.setStyle(tooltipElement, 'pointer-events', 'none');
      const triggerElement = this.getTriggerElement();
      const handlePointerMove = (event: PointerEvent) => {
        const target = event.target;

        if (!(target instanceof Node) || !triggerElement.contains(target)) {
          this.hideGlobalTooltip();
        }
      };

      document.addEventListener('pointermove', handlePointerMove, true);
      this.tooltipListenerCleanups.push(() => document.removeEventListener('pointermove', handlePointerMove, true));
      return;
    }

    // 添加鼠标进入tooltip事件 - 取消隐藏
    this.tooltipListenerCleanups.push(
      this.renderer.listen(tooltipElement, 'pointerenter', () => {
        this.isBridgeActive = false;
        this.clearHideTimer();
      }),
      this.renderer.listen(tooltipElement, 'mouseenter', () => {
        this.isBridgeActive = false;
        this.clearHideTimer();
      })
    );

    // 添加鼠标离开tooltip事件 - 延迟隐藏
    this.tooltipListenerCleanups.push(
      this.renderer.listen(tooltipElement, 'pointerleave', () => {
        this.isBridgeActive = false;
        this.scheduleHide(150);
      }),
      this.renderer.listen(tooltipElement, 'mouseleave', () => {
        this.isBridgeActive = false;
        this.scheduleHide(150);
      })
    );

    // 添加点击事件阻止冒泡
    this.tooltipListenerCleanups.push(
      this.renderer.listen(tooltipElement, 'click', (event: Event) => {
        event.stopPropagation();
      })
    );

    const handleDocumentPointer = (event: MouseEvent) => {
      const triggerElement = this.getTriggerElement();

      if (this.isEventInsideElement(event, triggerElement) || this.isEventInsideElement(event, tooltipElement)) {
        return;
      }

      this.closeFromOutside();
    };

    const handleDocumentWheel = (event: WheelEvent) => {
      const triggerElement = this.getTriggerElement();

      if (this.isEventInsideElement(event, triggerElement) || this.isEventInsideElement(event, tooltipElement)) {
        return;
      }

      this.hideGlobalTooltip();
    };

    document.addEventListener('pointerover', handleDocumentPointer, true);
    document.addEventListener('pointermove', handleDocumentPointer, true);
    document.addEventListener('mouseover', handleDocumentPointer, true);
    document.addEventListener('mousemove', handleDocumentPointer, true);
    document.addEventListener('click', handleDocumentPointer, true);
    document.addEventListener('wheel', handleDocumentWheel, true);
    this.tooltipListenerCleanups.push(() => {
      document.removeEventListener('pointerover', handleDocumentPointer, true);
      document.removeEventListener('pointermove', handleDocumentPointer, true);
      document.removeEventListener('mouseover', handleDocumentPointer, true);
      document.removeEventListener('mousemove', handleDocumentPointer, true);
      document.removeEventListener('click', handleDocumentPointer, true);
      document.removeEventListener('wheel', handleDocumentWheel, true);
    });

    this.tooltipListenerCleanups.push(
      this.renderer.listen('document', 'mouseleave', () => {
        this.hideGlobalTooltip();
      })
    );
  }

  private positionGlobalTooltip(): void {
    if (!this.globalTooltipElement) return;

    const triggerRect = this.getTriggerElement().getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 先设置初始位置以获取tooltip尺寸
    this.renderer.setStyle(this.globalTooltipElement, 'position', 'fixed');
    this.renderer.setStyle(this.globalTooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.globalTooltipElement, 'display', 'block');

    const tooltipRect = this.globalTooltipElement.getBoundingClientRect();

    // 计算最佳位置
    let top = triggerRect.bottom + 8;
    let left = triggerRect.left;

    if (this.placement === 'top') {
      top = triggerRect.top - tooltipRect.height - 8;
    } else if (this.placement === 'left') {
      top = triggerRect.top;
      left = triggerRect.left - tooltipRect.width - 8;
    } else if (this.placement === 'right') {
      top = triggerRect.top;
      left = triggerRect.right + 8;
    }

    // 检查右边界
    if (left + tooltipRect.width > viewportWidth - 20) {
      left = triggerRect.right - tooltipRect.width;
    }

    // 检查左边界
    if (left < 20) {
      left = 20;
    }

    // 检查下边界
    if (top + tooltipRect.height > viewportHeight - 20) {
      top = triggerRect.top - tooltipRect.height - 8;
    }

    // 检查上边界
    if (top < 20) {
      top = Math.min(triggerRect.bottom + 8, viewportHeight - tooltipRect.height - 20);
    }

    // 应用最终位置
    this.renderer.setStyle(this.globalTooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.globalTooltipElement, 'left', `${left}px`);
    this.renderer.setStyle(this.globalTooltipElement, 'z-index', '999999');
    this.renderer.setStyle(this.globalTooltipElement, 'visibility', 'visible');
  }

  private hideGlobalTooltip(): void {
    this.clearHideTimer();
    this.clearTooltipListeners();
    this.isBridgeActive = false;

    if (CustomTooltipComponent.activeTooltipCloser === this.tooltipCloser) {
      CustomTooltipComponent.activeTooltipCloser = null;
    }
    this.tooltipCloser = null;

    if (this.globalTooltipElement) {
      this.globalTooltipElement.remove();
      this.globalTooltipElement = null;
    }

    this.removeOrphanedGlobalTooltips();
  }

  onTooltipClick(event: Event): void {
    event.stopPropagation();
  }

  trackByCode(index: number, item: TooltipItem): string {
    return item.code;
  }
}
